import { useCallback } from 'react';

import { PrescriptionHistoryQuery } from '@dreryk/egabinet-prescription-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';
import { ContinuouslyUsedMedicineButton } from '~libs.desktop.shared.medicine';
import {
   PatientPrescriptionHistoryTable,
   PrescriptionHistoryRenderOptionsCell,
   usePrescriptionSelection,
} from '~libs.desktop.shared.patient-prescription-history';

import { PatientPrescriptionsHistoryLoader } from './PatientPrescriptionsHistory.loader';
import { PatientPrescriptionsHistoryFilters } from './PatientPrescriptionsHistoryFilters';
import { RepeatSelectedPrescriptionsAction } from './RepeatSelectedPrescriptionsAction';
import { StyledPatientPrescriptionsHistory } from './PatientPrescriptionsHistory.styled';

type PatientPrescriptionsHistoryProps = {
   readonly patientId: number;
};

export const PatientPrescriptionsHistory = (props: PatientPrescriptionsHistoryProps): JSX.Element => {
   const selection = usePrescriptionSelection();

   const handleMedicineProcessed = useCallback((): void => {
      refetchCqrsQuery(PrescriptionHistoryQuery);
   }, []);

   const renderOptionsCell = useCallback<PrescriptionHistoryRenderOptionsCell>(
      (_prescription, patientMedicine) => {
         const valid = patientMedicine.medicine.valid !== false;
         if (!valid) {
            return null;
         }
         return protectFeature(
            'issuingPrescriptions',
            <ContinuouslyUsedMedicineButton
               medicineId={patientMedicine.medicine.id}
               patientId={props.patientId}
               continuouslyUsedMedicineId={patientMedicine.patientContinuouslyUsedMedicineId}
               medicineName={patientMedicine.medicine.fullName}
               medicineValid={valid}
               onMedicineProcessed={handleMedicineProcessed}
            />,
            null
         );
      },
      [props.patientId, handleMedicineProcessed]
   );

   return (
      <StyledPatientPrescriptionsHistory direction={'column'} gap={'8px'}>
         <PatientPrescriptionsHistoryLoader>
            {({ doctors }) => (
               <PatientPrescriptionsHistoryFilters doctors={doctors}>
                  {({ filters }) => (
                     <PatientPrescriptionHistoryTable
                        patientId={props.patientId}
                        filters={filters}
                        selection={selection}
                        renderOptionsCell={renderOptionsCell}
                     />
                  )}
               </PatientPrescriptionsHistoryFilters>
            )}
         </PatientPrescriptionsHistoryLoader>
         {protectFeature(
            'issuingPrescriptions',
            <RepeatSelectedPrescriptionsAction
               patientId={props.patientId}
               selectedPrescriptionIds={selection.selectedPrescriptionIds}
            />,
            null
         )}
      </StyledPatientPrescriptionsHistory>
   );
};
